// SUR-R92/Q262: an archived type's chip renders identically. The eight
// color tokens match contest-api's AWARD_COLORS. Ported from
// `apps/mobile/src/accountHome/awardChips.ts`; the two apps do not share
// source, so this is the single web implementation of the same rule.
//
// Chips are non-interactive on the fan-profile screen and interactive on
// account home, so both the chip-detail-sheet helpers live here and the
// caller decides whether to use them.

export type typeAwardScope = "thisSeason" | "allTime";

export type typeAwardChip = {
  awardGrantId: string;
  poolId: string;
  contestName: string | null;
  kind: "medal" | "trophy";
  source: "earned" | "custom";
  name: string;
  color: string;
  isThisSeason: boolean;
};

export type typeAwardColorRole =
  | "primary"
  | "accent"
  | "success"
  | "warning"
  | "error"
  | "info"
  | "neutralStrong"
  | "neutralSoft";

const COLOR_ROLES: Record<string, typeAwardColorRole> = {
  color1: "primary",
  color2: "accent",
  color3: "success",
  color4: "warning",
  color5: "error",
  color6: "info",
  color7: "neutralStrong",
  color8: "neutralSoft",
};

export function chipsForScope(
  chips: readonly typeAwardChip[],
  scope: typeAwardScope,
): typeAwardChip[] {
  if (scope === "allTime") {
    return [...chips];
  }
  return chips.filter((chip) => chip.isThisSeason);
}

export function awardColorRole(color: string): typeAwardColorRole {
  return COLOR_ROLES[color] ?? "primary";
}

const CHIP_CLASS: Record<typeAwardColorRole, string> = {
  primary: "bg-primary-tint text-ink",
  accent: "bg-accent-tint text-ink",
  success: "bg-success-tint text-ink",
  warning: "bg-warning-tint text-ink",
  error: "bg-error-tint text-ink",
  info: "bg-info-tint text-ink",
  neutralStrong: "bg-surface-elevated text-ink",
  neutralSoft: "bg-surface text-ink",
};

export function awardChipClass(role: typeAwardColorRole): string {
  return CHIP_CLASS[role];
}

/** The fill and glyph pair for one award swatch.
 *
 *  The glyph is `text-surface`, not `text-on-primary`. `on-primary` is white
 *  in both modes, which is correct for its designed partner `primary-solid`,
 *  a fill that is one blue in both modes (`packages/theme/src/tokens.ts`,
 *  the `primarySolid` and `onPrimary` entries). These swatches use the
 *  flipping `bg-primary` family instead, so a fixed white glyph was right in
 *  light mode and wrong in dark: ISSUE-635 measured 2.10 to 3.18 there, and
 *  five of the six chromatic fills sat under `CONTRAST_FLOOR.nonText` of 3.
 *  `surface` flips with the fills, so one token clears the floor in both
 *  modes. Worst case is now 5.32, across all eight fills and both themes.
 *  Fix this here if it drifts; do not change the shared `onPrimary` token,
 *  which 24 other call sites pair correctly with a solid fill. */
const SWATCH_CLASS: Record<typeAwardColorRole, string> = {
  primary: "bg-primary text-surface",
  accent: "bg-accent text-surface",
  success: "bg-success text-surface",
  warning: "bg-warning text-surface",
  error: "bg-error text-surface",
  info: "bg-info text-surface",
  neutralStrong: "bg-ink-secondary text-surface",
  neutralSoft: "bg-surface-elevated text-ink",
};

export function awardSwatchClass(role: typeAwardColorRole): string {
  return SWATCH_CLASS[role];
}

/** The frame every award swatch carries, so a swatch whose fill equals the
 *  sheet's own background is still a visible control. `border-line`
 *  (--sm-color-border) measures 3.70:1 dark and 3.71:1 light against
 *  --sm-color-surface-elevated, clearing CONTRAST_FLOOR.nonText. The design
 *  frame's border-hairline measures 1.10:1 and 1.26:1 and does not. */
export const AWARD_SWATCH_FRAME_CLASS = "border border-line";

/** The selected swatch's ring. Offset 2px so it paints against the sheet,
 *  not against the swatch fill: focus-ring on surface-elevated is 3.14:1
 *  dark and 4.93:1 light.
 *
 *  Keep this ring even though ISSUE-635 fixed the check mark to 5.32:1 at
 *  worst. Two signals are carried on purpose. The glyph is
 *  `aria-hidden="true"` and sits inside the swatch, so it reads as "this
 *  one" only once the eye is already on the grid; the offset ring is what
 *  makes the selected swatch findable from across the sheet, and it is the
 *  signal that survives a fill token being retuned later. Removing it is a
 *  regression, and two standing tests will redden if it goes: the ring test
 *  in `awardSwatchContrast.test.ts` and the `color8` outline block in
 *  `tests/e2e/src/app/poolAwards.e2e.ts`. */
export const AWARD_SWATCH_SELECTED_CLASS =
  "outline-3 outline-offset-2 outline-focus-ring";

export function awardChipDetailLine(input: {
  name: string;
  poolName: string | null;
  contestName: string | null;
}): string {
  const parts = [input.name];
  if (input.poolName != null) {
    parts.push(input.poolName);
  }
  if (input.contestName != null) {
    parts.push(input.contestName);
  }
  return parts.join(" · ");
}

export function awardChipAccessibilityLabel(chip: typeAwardChip): string {
  return `${chip.name}, ${chip.kind}`;
}
